import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Printer, Zap, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const QZ_CDN = 'https://cdn.jsdelivr.net/npm/qz-tray@2.2.4/qz-tray.js';
const LS_KEY = 'manha_printer_config';

// Load QZ Tray SDK once. Returns global `qz`.
async function loadQZ() {
  if (window.qz) return window.qz;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = QZ_CDN;
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  // Signing not needed for local dev — set unsigned promise so QZ Tray won't block
  if (window.qz && !window.qz.security._verified) {
    window.qz.security.setCertificatePromise(function(resolve){resolve();});
    window.qz.security.setSignaturePromise(function(){return function(resolve){resolve();};});
  }
  return window.qz;
}

export default function PrinterSetup() {
  const [state, setState] = useState({ connected: false, printers: [], thermal: '', label: '', loading: false });

  // Read saved config (from browser + backend company_settings)
  useEffect(() => {
    const local = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
    setState(s => ({ ...s, thermal: local.thermal || '', label: local.label || '' }));
    axios.get(`${API_URL}/api/settings/printer-config`, { withCredentials: true }).then(({data}) => {
      setState(s => ({ ...s, thermal: s.thermal || data?.thermal || '', label: s.label || data?.label || '' }));
    }).catch(() => {});
  }, []);

  const connect = async () => {
    try {
      setState(s => ({ ...s, loading: true }));
      const qz = await loadQZ();
      if (!qz.websocket.isActive()) await qz.websocket.connect();
      const printers = await qz.printers.find();
      setState(s => ({ ...s, connected: true, printers, loading: false }));
      toast.success(`Connected — ${printers.length} printers detected`);
    } catch (err) {
      setState(s => ({ ...s, loading: false }));
      toast.error('QZ Tray not reachable. Install QZ Tray from qz.io/download and keep it running.');
    }
  };

  const persist = async () => {
    localStorage.setItem(LS_KEY, JSON.stringify({ thermal: state.thermal, label: state.label }));
    try {
      await axios.put(`${API_URL}/api/settings/printer-config`, { thermal: state.thermal, label: state.label }, { withCredentials: true });
      toast.success('Printer configuration saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Saved locally — backend rejected update');
    }
  };

  const testPrint = async (which) => {
    const printer = which === 'thermal' ? state.thermal : state.label;
    if (!printer) return toast.error(`Select the ${which} printer first`);
    try {
      const qz = await loadQZ();
      if (!qz.websocket.isActive()) await qz.websocket.connect();
      const cfg = qz.configs.create(printer);
      const isThermal = which === 'thermal';
      const data = isThermal ? [
        '\x1b\x40',                       // reset
        { type: 'raw', format: 'plain', data: 'MANHA DIGITAL\nYour Dreams, Our Deals\n----------------------------\nTest Print OK\n' + new Date().toLocaleString() + '\n----------------------------\n\n\n' },
        { type: 'raw', format: 'command', data: '\x1d\x56\x00' },  // cut
      ] : [
        { type: 'raw', format: 'plain', data: 'MANHA DIGITAL LABEL TEST\nACC: TEST-0001\n' },
      ];
      await qz.print(cfg, data);
      toast.success(`Test print sent to ${printer}`);
    } catch (err) {
      toast.error(`Print failed: ${err.message || err}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="printer-setup">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Printer Setup</h1>
        <p className="text-slate-400">Select thermal + label printers ONCE. Quick Print will use them without asking again.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <div><CardTitle className="text-white flex items-center"><Printer className="mr-2 h-5 w-5 text-amber-500" />QZ Tray Bridge</CardTitle>
            <p className="text-xs text-slate-400 mt-1">Install QZ Tray (free) from qz.io/download. It runs locally and exposes your OS printers to the browser.</p>
          </div>
          <Button onClick={connect} disabled={state.loading} data-testid="printer-connect-btn" className="bg-amber-500 hover:bg-amber-600 text-white">
            <Zap className="mr-2 h-4 w-4" />{state.connected ? 'Re-scan printers' : 'Connect to QZ Tray'}
          </Button>
        </CardHeader>
        {state.connected && (
          <CardContent className="space-y-4">
            {state.printers.length === 0 ? (
              <p className="text-slate-400 text-sm">No printers found. Add a printer in Windows/macOS settings and reconnect.</p>
            ) : (
              <>
                <PrinterPicker label="Thermal Receipt Printer (58mm / 80mm roll)"
                  printers={state.printers} value={state.thermal}
                  onChange={(v) => setState(s => ({ ...s, thermal: v }))}
                  onTest={() => testPrint('thermal')} testId="thermal" />
                <PrinterPicker label="Label / Barcode Printer (Box Label 1x2)"
                  printers={state.printers} value={state.label}
                  onChange={(v) => setState(s => ({ ...s, label: v }))}
                  onTest={() => testPrint('label')} testId="label" />
              </>
            )}
            <Button onClick={persist} data-testid="save-printer-config" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Save Configuration
            </Button>
          </CardContent>
        )}
        {!state.connected && (
          <CardContent>
            <div className="text-xs text-slate-400 flex items-start space-x-2">
              <HelpCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p>Once configured, receipts/labels print silently via QZ Tray.</p>
                <p className="mt-1">Currently saved: <b className="text-amber-500">{state.thermal || '(none)'}</b> · <b className="text-amber-500">{state.label || '(none)'}</b></p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function PrinterPicker({ label, printers, value, onChange, onTest, testId }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end gap-3 border border-slate-800 rounded-sm p-4 bg-slate-800/40">
      <div className="flex-1">
        <label className="text-xs text-slate-400 uppercase tracking-wide">{label}</label>
        <select value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={`printer-select-${testId}`}
          className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-sm h-9 px-3 text-sm text-white">
          <option value="">-- pick a printer --</option>
          {printers.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <Button variant="ghost" onClick={onTest} data-testid={`printer-test-${testId}`} className="border border-amber-500/40 text-amber-500 hover:bg-amber-500/10">
        Test Print
      </Button>
    </div>
  );
}

/**
 * Helper — call from any Receipt/Label button.
 * ```
 * import { quickPrint } from './settings/PrinterSetup';
 * await quickPrint('thermal', receiptText);
 * ```
 * Falls back to `window.print()` when QZ Tray isn't reachable.
 */
export async function quickPrint(kind, payload) {
  try {
    const cfg = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
    const printer = kind === 'thermal' ? cfg.thermal : cfg.label;
    if (!printer) throw new Error('Printer not configured — open Settings → Printers');
    const qz = await loadQZ();
    if (!qz.websocket.isActive()) await qz.websocket.connect();
    const c = qz.configs.create(printer);
    const data = typeof payload === 'string'
      ? [{ type: 'raw', format: 'plain', data: payload }]
      : payload;
    await qz.print(c, data);
    return true;
  } catch (err) {
    console.warn('quickPrint fallback:', err);
    if (typeof payload === 'string') {
      const w = window.open('', '_blank');
      w.document.write(`<pre>${payload}</pre>`);
      w.print();
    }
    return false;
  }
}
